import React, { useContext, useState }                     from 'react'
import {
  Checkbox, Container, FormControlLabel, Stack,
  TableBody, TableCell, TableFooter, TableHead,
  TableRow, Typography,
}                                                          from '@mui/material'
import { useTranslation }                                  from 'react-i18next'
import styled                                              from '@emotion/styled'
import Page                                                from '../../components/Page'
import { useApi }                                          from '../../utils/api'
import { fExportPrice, fPrice }                            from '../../utils/textutils'
import CommonStatisticsPage                                from '../../common/CommonStatisticsPage'
import { AppContext }                                      from '../../App'
import { User }                                            from "../../consts"

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&.OperatorRow .MuiTableCell-root': {
    color: '#ffffff',
  },
  '&.OperatorRow.Orange': {
    backgroundColor: '#f16e00',
  },
  '&.OperatorRow.Telekom': {
    backgroundColor: '#e20074',
  },
  '&.OperatorRow.O2': {
    backgroundColor: '#0090d0',
  },
  '&.OperatorRow.Swan, &.OperatorRow.4ka': {
    backgroundColor: '#fad210',
    '& .MuiTableCell-root': {
      color: '#000000',
    }
  },
}))

const highlightOperatorKey = 'highlightOperator'

const SharesStatisticsPage = () => {

  const api = useApi()
  const { t } = useTranslation()
  const { user } = useContext(AppContext)
  const [highlightOperator, setHighlightOperator] = useState(localStorage.getItem(highlightOperatorKey) === 'true')
  const setHighlightOperatorImpl = (e) => {
    localStorage.setItem(highlightOperatorKey, e.target.checked)
    setHighlightOperator(e.target.checked)
  }

  return (
    <Page title={t('Shares statistics')}>
      <Container component="main">
        <Typography variant="h4" gutterBottom>{t('Shares statistics')}</Typography>
        <CommonStatisticsPage
          fnApiLoadData={user.type === User.typeViewer ? api.viewer.messageShare.statistics : api.messageShare.statistics}
          pathPrefix={'/dashboard/statistics/shares'}
          defaultGroupBy={['service', 'type']}
          allowedGroupBy={['service', 'partner', 'operator', 'type', 'accounted']}
          exportFileName={'shares'}
          exportRow={(row, filter) => {
            let obj = {}
            if (filter.groupBy.includes('service')) {
              obj[t('Service')] = row.service?.name ?? ''
            }
            if (filter.groupBy.includes('partner')) {
              obj[t('Partner')] = row.partner?.name ?? ''
            }
            if (filter.groupBy.includes('operator')) {
              obj[t('Operator')] = row.operator?.name ?? ''
            }
            if (filter.groupBy.includes('type')) {
              obj[t('Type')] = t(`messageType_${row.type}`)
            }
            if (filter.groupBy.includes('accounted')) {
              obj[t('Accounted')] = row.accounted
            }
            obj[t('Count')] = row.count ?? 0
            obj[t('Price')] = fExportPrice(row.sumPrice ?? 0)
            return obj
          }}
          customOptions={
            <Stack key={'stack-option'} alignItems={'end'}>
              <FormControlLabel control={<Checkbox checked={highlightOperator} onChange={setHighlightOperatorImpl} />} label={t('Highlight operator')} />
            </Stack>
          }
        >
          <StatsTableHeader />
          <StatsTableBody highlightOperator={highlightOperator} />
          <StatsTableFooter />
        </CommonStatisticsPage>
      </Container>
    </Page>)
}

const StatsTableHeader = ({ t, filter }) => {

  return <TableHead>
    <TableRow>
      {filter.groupBy.includes('service') && <TableCell>{t('Service')}</TableCell>}
      {filter.groupBy.includes('partner') && <TableCell>{t('Partner')}</TableCell>}
      {filter.groupBy.includes('operator') && <TableCell>{t('Operator')}</TableCell>}
      {filter.groupBy.includes('type') && <TableCell>{t('Type')}</TableCell>}
      {filter.groupBy.includes('accounted') && <TableCell>{t('Accounted')}</TableCell>}
      <TableCell align={'right'}>{t('Count')}</TableCell>
      <TableCell align={'right'}>{t('Price')}&nbsp;€</TableCell>
    </TableRow>
  </TableHead>
}

const StatsTableBody = ({ t, filter, data, highlightOperator }) => {

  return <TableBody>
    {data.map((row, i) => <StyledTableRow className={highlightOperator ? `OperatorRow ${row.operator?.name}` : ''} key={`row-${i}`}>
      {filter.groupBy.includes('service') && <TableCell>{row.service?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('partner') && <TableCell>{row.partner?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('operator') && <TableCell>{row.operator?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('type') && <TableCell>{row.type ? t(`messageType_${row.type}`) : ''}</TableCell>}
      {filter.groupBy.includes('accounted') && <TableCell>{row.accounted ? row.accounted : ''}</TableCell>}
      <TableCell align={'right'}>{row.count ?? 0}</TableCell>
      <TableCell align={'right'}>{fPrice(row.sumPrice ?? 0)}</TableCell>
    </StyledTableRow>)}
  </TableBody>
}

const StatsTableFooter = ({ t, filter, data }) => {

  const sum = (prop) => data.map((row) => row[prop] ?? 0).reduce((acc, curr) => acc+curr, 0)
  const countOfEmptyCells = ['service', 'partner', 'operator', 'type', 'accounted'].map(groupBy => filter.groupBy.includes(groupBy)).filter(it => it).length - 1
  return <TableFooter>
    <TableRow>
      <TableCell component={'th'} scope="row">{t('Total')}</TableCell>
      {Array(countOfEmptyCells).fill().map((_, i) => <TableCell key={`cell-${i}`}>&nbsp;</TableCell>)}
      <TableCell align={'right'}>{sum('count')}</TableCell>
      <TableCell align={'right'}>{fPrice(sum('sumPrice'))}</TableCell>
    </TableRow>
  </TableFooter>
}

export default SharesStatisticsPage
